import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Plus, FileText, Clock, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";

const COUNTRIES = ["United Kingdom", "Canada", "Ireland", "United States", "Australia", "Other"];

function daysLeft(due) {
  if (!due) return null;
  const d = Math.ceil((new Date(due) - new Date()) / 86400000);
  return d;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState(null);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", due_date: "", study_country: "United Kingdom" });

  useEffect(() => {
    if (user?.role === "admin") navigate("/app/admin", { replace: true });
    else if (user?.role === "tutor") navigate("/app/tutor", { replace: true });
  }, [user, navigate]);

  const load = async () => {
    try {
      const { data } = await api.get("/assignments");
      setAssignments(data);
    } catch (e) { setAssignments([]); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) return toast.error("Give your assignment a title.");
    setCreating(true);
    try {
      const { data } = await api.post("/assignments", form);
      toast.success("Assignment created.");
      setOpen(false);
      navigate(`/app/assignments/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setCreating(false); }
  };

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Hi {user?.name?.split(" ")[0] || "there"} 👋</h1>
          <p className="mt-1 text-muted-foreground">Here are your assignments, ordered by what's next.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-assignment-btn" className="bg-[#0B5D3B] text-white hover:bg-[#08472D]">
              <Plus className="mr-2 h-4 w-4" /> New assignment
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Start a new assignment</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label htmlFor="title">Assignment title</Label>
                <Input id="title" data-testid="assignment-title-input" value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Equity & Trusts Essay" className="mt-1.5" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="due">Due date</Label>
                  <Input id="due" type="date" data-testid="assignment-due-input" value={form.due_date}
                    onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label>Country of study</Label>
                  <Select value={form.study_country} onValueChange={(v) => setForm({ ...form, study_country: v })}>
                    <SelectTrigger data-testid="assignment-country-input" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={creating} data-testid="create-assignment-submit" className="bg-[#0B5D3B] text-white hover:bg-[#08472D]">
                {creating ? "Creating…" : "Create & open"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {assignments === null ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}
        </div>
      ) : assignments.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card py-20 text-center" data-testid="empty-dashboard">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E8F5EE] text-[#0B5D3B] dark:bg-emerald-950 dark:text-[#25D366]">
            <Sparkles className="h-7 w-7" />
          </div>
          <h3 className="mt-4 font-display text-xl font-semibold">Start with an assignment</h3>
          <p className="mt-1 max-w-sm text-muted-foreground">Upload a brief and we'll map out exactly what to do next.</p>
          <Button onClick={() => setOpen(true)} className="mt-6 bg-[#0B5D3B] text-white hover:bg-[#08472D]">
            <Plus className="mr-2 h-4 w-4" /> New assignment
          </Button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {assignments.map((a) => {
            const dl = daysLeft(a.due_date);
            return (
              <button key={a.id} data-testid={`assignment-card-${a.id}`} onClick={() => navigate(`/app/assignments/${a.id}`)}
                className="group flex flex-col rounded-2xl border border-border bg-card p-6 text-left transition-colors duration-200 hover:border-[#0B5D3B]/40">
                <div className="flex items-start justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#E8F5EE] text-[#0B5D3B] dark:bg-emerald-950 dark:text-[#25D366]">
                    <FileText className="h-5 w-5" />
                  </div>
                  {dl !== null && (
                    <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${dl <= 3 ? "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300" : "bg-accent text-accent-foreground"}`}>
                      <Clock className="h-3 w-3" />{dl < 0 ? "overdue" : `${dl}d left`}
                    </span>
                  )}
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold leading-tight">{a.title}</h3>
                <p className="mt-1 text-xs text-muted-foreground">{a.study_country}</p>
                <div className="mt-auto flex items-center justify-between pt-5">
                  <span className="rounded-lg bg-[#0B5D3B]/10 px-2.5 py-1 text-xs font-semibold text-[#0B5D3B] dark:text-[#25D366]">
                    Next: {a.next_action}
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:translate-x-1" />
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
